import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Home, BookOpen, Phone, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { company } from "../data/mock";
import Seo from "../components/Seo";

const ThankYou = () => {
  const widgetRef = useRef(null);

  useEffect(() => {
    let tries = 0;
    let timer;
    const init = () => {
      if (window.Calendly && widgetRef.current) {
        widgetRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url: company.bookingUrl,
          parentElement: widgetRef.current,
        });
      } else if (tries < 50) {
        tries += 1;
        timer = setTimeout(init, 150);
      }
    };
    init();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-white">
      <Seo title="Thank You" description="Thanks for reaching out to Jellis Enterprise. Book a free strategy call now while your request is fresh." />

      {/* Hero confirmation */}
      <section className="relative navy-gradient overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="blob h-72 w-72 bg-blue-500 -top-10 right-10" />
        <div className="relative mx-auto max-w-3xl px-6 py-20 md:py-24 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/10 ring-8 ring-white/5">
            <CheckCircle2 className="h-11 w-11 text-emerald-400" strokeWidth={2.2} />
          </div>
          <h1 className="mt-7 font-display text-4xl md:text-5xl font-extrabold text-white leading-tight">
            Thank you — your message is on its way!
          </h1>
          <p className="mt-5 text-lg text-blue-100/80">
            A member of our team will reach out within one business day. We've also sent a confirmation to your inbox.
          </p>
        </div>
      </section>

      {/* Book a call front and center */}
      <section className="section-pad">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Want to talk sooner?</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">
              Book your free strategy call now
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Grab a time that works for you below — it's free, with zero obligation.
            </p>
          </div>

          {/* Inline Calendly */}
          <div className="mt-10 rounded-3xl border border-slate-200 shadow-xl overflow-hidden bg-white">
            <div ref={widgetRef} style={{ minWidth: "320px", height: "700px" }} />
          </div>

          {/* Fallback button */}
          <div className="mt-6 text-center">
            <a href={company.bookingUrl} target="_blank" rel="noreferrer">
              <Button size="lg" className="brand-gradient text-white font-bold group">
                Open the scheduler
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Next steps */}
      <section className="pb-24">
        <div className="mx-auto max-w-5xl px-6 grid sm:grid-cols-3 gap-5">
          <Link to="/" className="rounded-2xl border border-slate-200 bg-white p-6 card-lift text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Home className="h-6 w-6" /></div>
            <p className="mt-4 font-display font-bold text-slate-900">Back to home</p>
            <p className="mt-1 text-sm text-slate-500">Explore what we do</p>
          </Link>
          <Link to="/blog" className="rounded-2xl border border-slate-200 bg-white p-6 card-lift text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><BookOpen className="h-6 w-6" /></div>
            <p className="mt-4 font-display font-bold text-slate-900">Read the blog</p>
            <p className="mt-1 text-sm text-slate-500">Free marketing tips</p>
          </Link>
          <a href={company.phoneHref} className="rounded-2xl border border-slate-200 bg-white p-6 card-lift text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Phone className="h-6 w-6" /></div>
            <p className="mt-4 font-display font-bold text-slate-900">Call us</p>
            <p className="mt-1 text-sm text-slate-500">{company.phone}</p>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ThankYou;
